/**
 * Note Editor Form
 *
 * Form content for editing/creating notes.
 * Extracted from NoteEditDialog.tsx for use in the in-drawer editor panel.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNote, useUpdateNote, useCreateNote } from '@/hooks/useMyNotes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { X, Loader2 } from 'lucide-react';
import { NotepadEditorMode } from './useNotepadEditorState';
import { NoteChecklist } from './NoteChecklistItem';

interface NoteEditorFormProps {
  mode: NotepadEditorMode;
  noteId: string | null;
  onSaved: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface NoteEditorFormRef {
  save: () => void;
}

const DEFAULT_NOTE_VALUES = {
  title: '',
  content: '',
  tags: [] as string[],
};

export const NoteEditorForm = forwardRef<NoteEditorFormRef, NoteEditorFormProps>(
  function NoteEditorForm({ mode, noteId, onSaved, onCancel, onDirtyChange }, ref) {
    const { t } = useTranslation(['notepad', 'common']);
    const updateNote = useUpdateNote();
    const createNote = useCreateNote();
    const titleInputRef = useRef<HTMLInputElement>(null);

    // Track initial values for dirty detection
    const initialValuesRef = useRef<typeof DEFAULT_NOTE_VALUES | null>(null);

    // Fetch note data if editing
    const { data: note, isLoading: isLoadingNote } = useNote(noteId || '');

    // Form state
    const [title, setTitle] = useState(DEFAULT_NOTE_VALUES.title);
    const [content, setContent] = useState(DEFAULT_NOTE_VALUES.content);
    const [tags, setTags] = useState<string[]>(DEFAULT_NOTE_VALUES.tags);
    const [tagInput, setTagInput] = useState('');

    // Initialize form when note loads (edit mode)
    useEffect(() => {
      if (mode === 'edit' && note) {
        const initialValues = {
          title: note.title,
          content: note.content || '',
          tags: note.tags || [],
        };
        setTitle(initialValues.title);
        setContent(initialValues.content);
        setTags(initialValues.tags);
        initialValuesRef.current = initialValues;
      } else if (mode === 'create') {
        // Reset to defaults for create mode
        setTitle(DEFAULT_NOTE_VALUES.title);
        setContent(DEFAULT_NOTE_VALUES.content);
        setTags(DEFAULT_NOTE_VALUES.tags);
        initialValuesRef.current = DEFAULT_NOTE_VALUES;
      }
    }, [mode, note]);

    // Track dirty state
    useEffect(() => {
      if (!initialValuesRef.current) return;

      const initial = initialValuesRef.current;
      const isDirty =
        title !== initial.title ||
        content !== initial.content ||
        JSON.stringify(tags) !== JSON.stringify(initial.tags);

      onDirtyChange?.(isDirty);
    }, [title, content, tags, onDirtyChange]);

    // Auto-focus title input when form mounts
    useEffect(() => {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
        if (mode === 'edit') {
          titleInputRef.current?.select();
        }
      }, 100);
      return () => clearTimeout(timer);
    }, [mode, note]);

    const handleSave = useCallback(() => {
      // Validate
      if (!title.trim()) {
        return;
      }

      const input = {
        title: title.trim(),
        content: content.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
      };

      if (mode === 'edit' && noteId) {
        // Update existing note
        updateNote.mutate({ noteId, input }, { onSuccess: onSaved });
      } else {
        // Create new note
        createNote.mutate(input, { onSuccess: onSaved });
      }
    }, [title, content, tags, mode, noteId, updateNote, createNote, onSaved]);

    // Expose save method via ref (must be after handleSave is defined)
    useImperativeHandle(
      ref,
      () => ({
        save: handleSave,
      }),
      [handleSave]
    );

    const handleAddTag = () => {
      const tag = tagInput.trim();
      if (tag && !tags.includes(tag)) {
        setTags([...tags, tag]);
        setTagInput('');
      }
    };

    const handleRemoveTag = (tag: string) => {
      setTags(tags.filter((t) => t !== tag));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSave();
      }
    };

    const isSaving = updateNote.isPending || createNote.isPending;

    // Show loading state while fetching note in edit mode
    if (mode === 'edit' && isLoadingNote) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    // If editing but note not found
    if (mode === 'edit' && !note && !isLoadingNote) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <p className="text-muted-foreground">{t('notepad:note.notFound', 'Note not found')}</p>
          <Button variant="outline" onClick={onCancel} className="mt-4">
            {t('common:actions.back', 'Back')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t('notepad:note.title', 'Title')}</Label>
            <Input
              ref={titleInputRef}
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('notepad:note.titlePlaceholder', 'Note title')}
              className="text-base"
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">{t('notepad:note.content', 'Content')}</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('notepad:note.contentPlaceholder', 'Write your note here...')}
              className="min-h-[200px] text-base resize-none"
              rows={10}
            />
          </div>

          {/* Checklist (read-only in edit mode, shows existing items) */}
          {mode === 'edit' && note?.checklist && note.checklist.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label>{t('notepad:note.checklist', 'Checklist')}</Label>
                <NoteChecklist noteId={note.id} items={note.checklist} />
              </div>
            </>
          )}

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">{t('notepad:note.tags', 'Tags')}</Label>
            <div className="flex gap-2">
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddTag();
                  }
                }}
                placeholder={t('notepad:note.tagPlaceholder', 'Add tag (e.g., meeting, ideas)')}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                {t('common:actions.add', 'Add')}
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-destructive"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Keyboard hint */}
          <div className="text-xs text-muted-foreground text-center pt-2">
            Press <kbd className="px-1 py-0.5 bg-muted rounded">⌘</kbd>
            <kbd className="px-1 py-0.5 bg-muted rounded ml-1">Enter</kbd> to save
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 px-6 py-4 border-t bg-background">
          <Button variant="outline" onClick={onCancel}>
            {t('common:actions.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!title.trim() || isSaving}>
            {isSaving ? t('common:actions.saving', 'Saving...') : t('common:actions.save', 'Save')}
          </Button>
        </div>
      </div>
    );
  }
);
